import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { MessageCircle, FileText, Calendar } from "lucide-react";

const actionCards = [
  {
    title: "Chat com super IA Jurídica",
    subtitle: "Faça perguntas jurídicas no chat",
    description: "Obtenha respostas especializadas para suas dúvidas legais",
    icon: MessageCircle,
    color: "bg-blue-500",
    textColor: "text-blue-600",
    bgColor: "bg-blue-50",
    route: "/chat-juridico",
    available: true
  },
  {
    title: "Crie documentos com IA",
    subtitle: "Construa documentos em segundos",
    description: "Mais de 220 modelos para agilizar seu trabalho. Elabore de forma rápida e inteligente com a IA",
    icon: FileText,
    color: "bg-green-500", 
    textColor: "text-green-600",
    bgColor: "bg-green-50",
    route: "/documentos",
    available: true
  },
  {
    title: "Agenda",
    description: "Visualize suas atividades do mês",
    icon: Calendar,
    color: "bg-indigo-500",
    textColor: "text-indigo-600",
    bgColor: "bg-indigo-50",
    route: "/agenda",
    available: true,
    isAgenda: true
  }
];

interface DashboardCardsProps {
  onAgendaToggle?: () => void;
}

export function DashboardCards({ onAgendaToggle }: DashboardCardsProps) {
  const navigate = useNavigate();

  const handleCardClick = (card: typeof actionCards[0]) => {
    if ((card as any).isAgenda) {
      onAgendaToggle?.();
    } else if (card.available) {
      navigate(card.route);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {actionCards.map((card, index) => {
        const Icon = card.icon;
        return (
              <Card 
            key={index} 
            className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
              index === 0 ? 'border border-blue-200' : 
              index === 1 ? 'border border-green-200' : 
              index === 2 ? 'border border-indigo-200' : 'border-0'
            } ${card.bgColor} ${
              !card.available ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
            }`}
            onClick={() => handleCardClick(card)}
            data-tour={
              index === 0 ? 'chat-card' :
              index === 1 ? 'documents-card' :
              'agenda-card'
            }
          >
            <CardContent className="p-6 space-y-4">
              {index === 0 || index === 1 || index === 2 ? (
                // Layout especial para o primeiro e segundo card
                <>
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-lg ${card.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className={`font-semibold ${card.textColor}`}>
                      {card.title}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <h4 className={`font-medium ${card.textColor} text-sm`}>
                      {(card as any).subtitle}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {card.description}
                    </p>
                  </div>
                </>
              ) : (
                // Layout padrão para os outros cards
                <>
                  <div className={`w-12 h-12 rounded-lg ${card.color} flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="space-y-2">
                    <h3 className={`font-semibold ${card.textColor} flex items-center gap-2`}>
                      {card.title}
                      {!card.available && (
                        <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                          Em breve
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {card.description}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}